/**
 * Size Guides Store
 *
 * Zustand-based store for the channel-neutral size-guide domain, wired with the
 * command-driven state engine. See SDD `docs/prs/TV1-116/sdd.md` §"State Design".
 *
 * Usage in components:
 * ```typescript
 * import {
 *   useSizeGuidesSelector,
 *   useSizeGuidesDispatch,
 *   useSizeGuidesPolicies,
 *   createSizeGuidesPolicies
 * } from '@domains/sizeGuides';
 * import useBackend from '@contexts/useBackend/useBackend';
 *
 * function SizeGuidesGallery() {
 *   const { backend } = useBackend();
 *   const dispatch = useSizeGuidesDispatch();
 *
 *   // Initialize policies with backend
 *   useSizeGuidesPolicies(createSizeGuidesPolicies(backend));
 *
 *   // Select state
 *   const guides = useSizeGuidesSelector(s => s.data.guides);
 *
 *   // Dispatch commands
 *   const handleSelect = ( guideId: string ) => {
 *     dispatch(
 *       { type: 'sizeGuides/editor/select', payload: { guideId } },
 *       { source: 'user', timestamp: Date.now() }
 *     );
 *   };
 * }
 * ```
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { initialState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { SizeGuidesCommand } from './commands';
import {
  createSizeGuidesDataPolicy,
  createSizeGuideCrudPolicy
} from './policies';

// Create the store (no policies passed here - they need backend)
const {
  useSelector,
  useErrors,
  usePolicies,
  getState,
  dispatch
} = createStore({
  name: 'sizeGuides',
  initialState,
  handlers,
  invariants
});

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates (`gateCommandByCapability`, ticket #11) can
 * authorize them. The engine core stays auth-agnostic; this web-app boundary
 * supplies the actor. An explicit `meta.user` (e.g. from tests) is left
 * untouched. Mirrors `useWarehousesDispatch` / `useAccountDispatch`.
 *
 * CRITICAL: this is a capability-gated domain. Without this wrapper,
 * `invariants.canApply` would see `can( undefined, cap )` and silently block
 * EVERY user-sourced privileged command (`crud/upsert`, `crud/delete`) — the
 * dispatch no-ops with only a console.warn and the feature appears dead. See
 * project memory "Capability-gated CDSE domains need a user-attaching dispatch
 * wrapper".
 */
const useSizeGuidesDispatch = (): Dispatch<SizeGuidesCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<SizeGuidesCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Policy factory function.
 * Creates all policies with backend dependency. Composes the data
 * (query + signal-subscribe readmodel) and CRUD-drain policies. Mirrors
 * `createWarehousesPolicies` — only depends on `backend`; component-level
 * concerns (contexts, props) are composed at the page via `useMemo`.
 *
 * @param backend Backend instance from useBackend hook
 * @returns Array of policies to pass to useSizeGuidesPolicies
 */
export const createSizeGuidesPolicies = ( backend: ClientLibraryType ) => [
  createSizeGuidesDataPolicy( backend ),
  createSizeGuideCrudPolicy( backend )
];

// Export hooks with domain-specific names
export {
  useSelector as useSizeGuidesSelector,
  useErrors as useSizeGuidesErrors,
  useSizeGuidesDispatch,
  usePolicies as useSizeGuidesPolicies,
  getState as getSizeGuidesState,
  dispatch as dispatchSizeGuidesCommand
};

/**
 * Re-export selectors for convenience
 */
export { selectors } from './state';
